import moment from 'moment';
import { _config } from 'Utilities/envConfig';
import { error } from 'Utilities/log';
export const _getMoment = value => _isUnixTime(value) ? moment.unix(value) : moment(value);
export const _getMomentUTC = value => _getMoment(value).utc();

// This assumes that we will NEVER use milliseconds for timestamps
export const _isUnixTime = value => {
  return Number.isInteger(value);
};
const dateFormats = {
  '1': {
    dashed: 'M-D-YYYY',
    fullDashed: 'MM-DD-YYYY',
    slashed: 'M/D/YYYY',
    fullSlashed: 'MM/DD/YYYY',
    long: 'MMM D, YYYY',
    longer: 'MMMM D, YYYY'
  },
  '2': {
    dashed: 'D-M-YYYY',
    fullDashed: 'DD-MM-YYYY',
    slashed: 'D/M/YYYY',
    fullSlashed: 'DD/MM/YYYY',
    long: 'D MMM YYYY',
    longer: 'D MMMM YYYY'
  },
  '3': {
    dashed: 'YYYY-M-D',
    fullDashed: 'YYYY-MM-DD',
    slashed: 'YYYY/M/D',
    fullSlashed: 'YYYY/MM/DD',
    long: 'YYYY MMM D',
    longer: 'YYYY MMMM D'
  }
};
const plainDateFormat = 'YYYY-MM-DD';
const timeFormats = {
  '1': {
    hour: 'ha',
    hourMinute: 'h:mm a'
  },
  '2': {
    hour: 'HH:00',
    hourMinute: 'H:mm'
  }
};
const currentDateFormats = dateFormats['1'];
const currentTimeFormats = timeFormats['1'];
const valuesToMoment = ({
  year,
  month = 1,
  day
}) => moment.utc({
  year,
  month: Number(month) - 1,
  day
});
export const getLongerDate = (date = moment()) => _getMoment(date).format(currentDateFormats.longer);
export const getDashedDate = (date = moment()) => _getMoment(date).format(currentDateFormats.dashed);
export const getFullDashedDate = (date = moment()) => _getMoment(date).format(currentDateFormats.fullDashed);
export const getSlashedDate = (date = moment()) => _getMoment(date).format(currentDateFormats.slashed);
export const getFullSlashedDate = (date = moment()) => _getMoment(date).format(currentDateFormats.fullSlashed);
export const getDashedDateUTCPlain = (date = moment()) => _getMomentUTC(date).format(plainDateFormat);
export const getDashedDateUTC = (date = moment()) => _getMomentUTC(date).format(currentDateFormats.dashed);
export const getSlashedDateUTC = (date = moment()) => _getMomentUTC(date).format(currentDateFormats.slashed);
export const getUnixTimstamp = (value = moment()) => _getMoment(value).unix();
export const getUtcUnixFromDate = (value = moment.utc()) => _getMomentUTC(value).unix();
export const dayOfWeekOptions = () => {
  const dayOfWeek = [];
  for (let i = 0, d = moment.localeData().firstDayOfWeek(); i < 7; i++, d = ++d % 7) {
    dayOfWeek.push({
      value: d,
      name: moment.weekdays(d)
    });
  }
  return dayOfWeek;
};
export const getDateTimeParts = timestamp => {
  const date = _getMoment(timestamp);
  return {
    month: date.month(),
    date: date.date(),
    year: date.year(),
    hour: date.hour(),
    minute: date.minute(),
    second: date.second()
  };
};
export const getDateTimePartsUTC = timestamp => {
  const date = _getMomentUTC(timestamp);
  return {
    month: date.month(),
    date: date.date(),
    year: date.year(),
    hour: date.hour(),
    minute: date.minute(),
    second: date.second()
  };
};

/**
 * Generates a format based on user settings to be used for moment formatting
 *
 * @param {string} format Either a key corresponding to a custom date format
 * @param {string} [time] A time format option to add to the end
 * @param {string} [seperator] The seperator to use between date and time, otherwise a space is used
 */
export const makeFormatString = (format, time = '', seperator = '') => {
  let result = '';
  if (currentDateFormats[format]) {
    result += currentDateFormats[format];
  } else if (format) {
    return format;
  }
  if (currentTimeFormats[time]) {
    result += result.length > 0 ? seperator ? ` ${seperator} ` : ' ' : '';
    result += currentTimeFormats[time];
  }
  return result;
};
export const timestampNow = () => moment().unix();
export const timestampNowUTC = () => moment.utc().unix();

/**
 * Produces a date string ready to be sent to the server
 *
 * @param {moment.MomentInput} value
 */
export const createDateStringFromJsDate = (value = moment()) => moment(value).format('YYYY-MM-DD');
export const monthOptions = () => moment.months('MMMM') // pass in a format string to get Polish months in Nominative
.map((month, index) => ({
  value: index + 1,
  name: month
}));

/**
 * Returns the difference of dateA and dateB by the specified measurement unit
 *
 * @param {moment.MomentInput} dateA Timestamp that is being subtracted. Defaults to now()
 * @param {moment.MomentInput} dateB Timestamp amount to subtract. Defaults to now()
 * @param {moment.unitOfTime.Diff} measurement The unit of measurement to use. Defaults to 'day'
 * @param {boolean} noTruncate Flag to not truncate the result. Defaults to false
 */
export const timeStampDiff = (dateA = moment(), dateB = moment(), measurement = 'days', noTruncate = false) => {
  const a = _getMoment(dateA);
  const b = _getMoment(dateB);
  return b.diff(a, measurement, noTruncate);
};

/**
 * Returns the difference of dateA and dateB by the specified measurement unit
 *
 * @param {moment.MomentInput} dateA Timestamp that is being subtracted. Defaults to now()
 * @param {moment.MomentInput} dateB Timestamp amount to subtract. Defaults to now()
 * @param {moment.unitOfTime.Diff} measurement The unit of measurement to use. Defaults to 'day'
 * @param {boolean} noTruncate Flag to not truncate the result. Defaults to false
 */
export const timeStampDiffUTC = (dateA = moment.utc(), dateB = moment.utc(), measurement = 'days', noTruncate = false) => {
  const a = _getMomentUTC(dateA);
  const b = _getMomentUTC(dateB);
  return b.diff(a, measurement, noTruncate);
};

/**
 * Formats a timestamp to the specified format type
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {string} format A date format type or compatible moment format string
 * @param {string} [time] A time format option to add to the end
 * @param {string} [seperator] The seperator to use between date and time, otherwise a space is used
 */
export const timeStampFormat = (value, format, time, seperator) => _getMoment(value).format(makeFormatString(format, time, seperator));
/**
 * Formats a UTC timestamp to the specified format type
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {string} format A date format type or compatible moment format string
 * @param {string} [time] A time format option to add to the end
 * @param {string} [seperator] The seperator to use between date and time, otherwise a space is used
 */
export const timeStampFormatUTC = (value, format, time, seperator) => _getMomentUTC(value).format(makeFormatString(format, time, seperator));

/**
 * Returns a moment marked as UTC converted from a unix timestamp received in
 * an api response.  Any format commands performed on this moment will display
 * the proper units based on what the device/patient time represets.  It should
 * not be converted to local time.
 *
 * @param {moment.MomentInput} value The timestamp to use
 */
export const timeStampToUTCMoment = value => _getMomentUTC(value);

/**
 * Returns a localized relative time for the timestamp to either a specified time or now
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {moment.MomentInput} [when] A time for computing relativity. Defaults to now()
 */
export const timeStampFromNow = (value, when = moment().utc(), noSuffix = false) => {
  return _getMoment(value).from(when, noSuffix);
};
export const timeStampFromNowUTC = (value, when = moment().utc(), noSuffix = false) => {
  return _getMomentUTC(value).from(when, noSuffix);
};

/**
 * Returns a localized time (Unix) for the timestamp in the future
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {number} amount The number of time too add, defaults to 7.
 * @param {string} type The type of time to add, defaults to days.
 */
export const timeStampAdd = (value = moment(), amount = 7, type = 'd') => {
  return _getMoment(value).add(amount, type).unix();
};

/**
 * Returns a localized time (Unix) for the timestamp in the future
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {number} amount The number of time too add, defaults to 7.
 * @param {string} type The type of time to add, defaults to days.
 */
export const timeStampAddUTC = (value = moment.utc(), amount = 7, type = 'd') => {
  return _getMomentUTC(value).add(amount, type).unix();
};

/**
 * Returns a localized time (Unix) for the timestamp in the past
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {number} amount The number of time too subtract, defaults to 7.
 * @param {string} type The type of time to subtract, defaults to days.
 */
export const timeStampSubtractUTC = (value = moment.utc(), amount = 7, type = 'd') => {
  return _getMomentUTC(value).subtract(amount, type).unix();
};

/**
 * Returns a full localized timestamp using user format settings
 *
 * @param {moment.MomentInput} value The timestamp to use
 */
export const timeStampFull = value => moment(value).format(makeFormatString('long', 'hourMinute', '@'));

/**
 * Returns a full localized timestamp (unix from server) using user format settings
 *
 * @param {moment.MomentInput} value The timestamp to use
 */
export const timeStampFullUTC = value => _getMomentUTC(value).format(makeFormatString('long', 'hourMinute', '@'));

/**
 * Returns a localized timestamp formated as relative if within the specified amount of seconds, or the full timestamp using user settings
 * @param {moment.MomentInput} value The timestamp to use
 * @param {string} [format] Formatting for full timestamp. Defaults to long with time
 * @param {number} [diff] The difference for relative checking. Defaults to 7 days
 */
export const timeStampRelative = (value, format = makeFormatString('long', 'hourMinute', '@'), diff = 60 * 60 * 24 * 7) => {
  if (Math.abs(moment().diff(_getMoment(value), 'seconds')) > diff) {
    return timeStampFormat(value, format);
  } else {
    return timeStampFromNow(value);
  }
};
export const timeStampRelativeUTC = (value, format = makeFormatString('long', 'hourMinute', '@'), diff = 60 * 60 * 24 * 7) => {
  if (Math.abs(moment().diff(_getMomentUTC(value), 'seconds')) > diff) {
    return timeStampFormatUTC(value, format);
  } else {
    return timeStampFromNowUTC(value);
  }
};

/**
 * This will get the string formatted according to the current time setting (24hr or 12hr)
 *
 * example: 2am, 14:00, 2:13 pm, etc.
 * @param {int} hour The hour in 24 hour notation
 * @param {int} minute The minutes (this is optional and will change the format)
 */
export const timeString = (hour, minute) => {
  let format = currentTimeFormats.hour;
  if (minute !== undefined && minute !== null) {
    format = currentTimeFormats.hourMinute;
  }
  minute = minute || 0;
  return moment(`${hour} ${minute}`, 'H mm').format(format);
};

/**
 * This will return an array of strings that will be used
 *  at the top of a graph
 * @param  {int} sep Used to determine the hour spacing between strings
 * @param  {int} startingHour Used to determine when to start at, defaults to hour 0
 * @return {[string]} Array of strings to print on the graph
 */
export const timeTicks = (sep, startingHour) => {
  startingHour = startingHour || 0;
  const ret = [];
  for (let i = 0; i <= 25; i += sep) {
    ret.push(timeString((i + startingHour) % 24));
  }
  return ret;
};

/**
 * Pass in the dates that you want to see if they are the same day
 * if you don't pass in a second parameter, then it will check if it is today
 * @param {*} dateA a date that is parse-able by moment.js
 * @param {*} dateB a date that is parse-able by moment.js - defaults to today
 */
export const isSameDate = (dateA, dateB = moment()) => moment(dateA).isSame(dateB, 'day');

/**
 * Pass in the timestamps that you want to see if they are the same day
 * if you don't pass in a second parameter, then it will check if it is today
 * @param {*} dateA a date that is parse-able by moment.js
 * @param {*} dateB a date that is parse-able by moment.js - defaults to today
 */
export const isTimestampSameDate = (dateA, dateB = timestampNow()) => moment.unix(dateA).isSame(moment.unix(dateB), 'day');

/**
 * Pass in the UTC timestamps that you want to see if they are the same day
 * if you don't pass in a second parameter, then it will check if it is today
 * @param {*} dateA a UTC date that is parse-able by moment.js
 * @param {*} dateB a UTC date that is parse-able by moment.js - defaults to today
 */
export const isTimestampSameDateUTC = (dateA, dateB = timestampNowUTC()) => _getMomentUTC(dateA).isSame(_getMomentUTC(dateB), 'day');

/**
 * Pass in the timestamps that you want to see if they are within yesterday
 * if you don't pass in a second parameter, then it will check if it is today
 * @param {*} dateA a date that is parse-able by moment.js
 * @param {*} dateB a date that is parse-able by moment.js - defaults to today
 */
export const isTimestampWithinYesterday = (dateA, dateB = timestampNow()) => moment.unix(dateB).diff(moment.unix(dateA), 'd') <= 1;

/**
 * Converts a unix timestamp into a unix timestamp at 00:00:00 of that same day
 *
 * NOTE - the unix timestamp is assumed to be utc and will be returned as utc
 * @param {int} date the unix value of the timestamp
 */
export const timestampToMidnight = (date = moment.utc()) => _getMoment(date).startOf('day').unix();

/**
 * Converts a unix timestamp into a unix timestamp at 00:00:00 of that same day
 *
 * NOTE - the unix timestamp is assumed to be utc and will be returned as utc
 * @param {int} date the unix value of the timestamp
 */
export const timestampToMidnightUTC = (date = moment.utc()) => _getMoment(date).utc(!_isUnixTime(date)).startOf('day').unix();

/**
 * Converts a unix timestamp into a date object that when formatted
 * will represent the way the date was intended to present from the
 * device / patient perspective it relates to.
 *
 * NOTE: this will return a javascript Date object that can be used
 * for datepickers , etc.  This is a special use case for displaying
 * the proper date with things that need a date object.
 *
 * @param {int} date the unix value returned from the API
 */
export const timestampToDate = date => {
  const m = _getMoment(date);

  // then we create new date which will think it is local
  // but set as that time so controls display the proper
  // month/day etc.
  return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.seconds(), m.milliseconds());
};

/**
 * Converts a unix timestamp into a date object that when formatted
 * will represent the way the date was intended to present from the
 * device / patient perspective it relates to.
 *
 * NOTE: this will return a javascript Date object that can be used
 * for datepickers , etc.  This is a special use case for displaying
 * the proper date with things that need a date object.
 *
 * @param {int} date the unix value returned from the API
 */
export const timestampToUTCDate = date => {
  // we tell moment to treat this as utc so when you ask
  // for the day, it is in reference to UTC which is what
  // represents the patient/device date
  const m = _getMomentUTC(date);

  // then we create new date which will think it is local
  // but set as that time so controls display the proper
  // month/day etc.
  return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.seconds(), m.milliseconds());
};

/**
 * This will take an object of a date (just year, month, day properties) and return a UTC timestamp.
 * NOTE: this expects the month to be 1-12, not 0 indexed like moment expects. This function will subtract
 * 1 from the month value for you.
 *
 * @param {object} dateObj an object that has `year`, `month`, and `day` properties
 */
export const valuesToTimestampUTC = dateObj => {
  return valuesToMoment(dateObj).unix();
};
export const dateToMidnightUTC = (date = moment()) => moment.utc(_getMoment(date).format(plainDateFormat)).unix();

/**
 * Takes an object of year, month, and day and determines whether that is an actual date
 *
 * @param {*} dateObj Object with properties: `year`, `month`, `day`
 */
export const isDateFromPartsValid = dateObj =>
// Moment considers a day value of 0 valid, so this check ensures we don't accept 0 as a valid day
dateObj.day !== 0 && valuesToMoment(dateObj).isValid() &&
// Ensure values are whole numbers
Number.isInteger(Number(dateObj.year)) && Number.isInteger(Number(dateObj.month)) && (dateObj.day === undefined || Number.isInteger(Number(dateObj.day)));

/**
 * Returns true if the given previous date's timestamp is before the given future date's timestamp
 * Future date will default to a current timestamp if no param is given
 *
 * @param {object|number|string} previousDate - Timestamp string/number or object with 3 fields: month, day, year.
 * @param {object|number|string} futureDate - Timestamp string/number or object with 3 fields: month, day, year.
 */
export const isFutureDate = (previousDate, futureDate = timestampNowUTC()) => {
  // verify given dates are in timestamp format
  previousDate = previousDate.year ? valuesToTimestampUTC(previousDate) : previousDate;
  futureDate = futureDate.year ? valuesToTimestampUTC(futureDate) : futureDate;
  return previousDate > futureDate;
};

// Since we are calling this in one place we are just rogue casting moment instead of adding the function as a typing
const _createFromInputFallback = moment.createFromInputFallback;
moment.createFromInputFallback = config => {
  if (!_config.production) {
    error('Detected a momentjs fallback to new Date()!');
    alert('Detected a momentjs fallback to new Date()!');
  }
  _createFromInputFallback(config);
};